import { useEffect, useRef, useState } from "react";
import { FLAVOURS } from "@/data/content";
import { AnimatePresence, motion } from "framer-motion";

const GRID = { 12: [4, 3], 24: [6, 4], 50: [10, 5] };
const W = 560;
const D = 330;
const WALL = 64;
const SCENE_W = 640;
const SCENE_H = 400;

const Wall = ({ style, children, shade = "#1B1530" }) => (
  <div className="absolute grid place-items-center" style={{ background: shade, backfaceVisibility: "visible", ...style }}>{children}</div>
);

const Bag = ({ f, x, rowY, cw, bh, i, flat }) => (
  <motion.div className="absolute left-0 top-0 flex justify-center" style={{ width: cw, transformStyle: "preserve-3d" }}
    initial={{ x, y: rowY - bh, z: 420, opacity: 0 }}
    animate={{ x, y: rowY - bh, z: 0, opacity: 1 }}
    exit={{ z: 380, opacity: 0, transition: { duration: 0.35 } }}
    transition={{ z: { type: "spring", stiffness: 210, damping: 15, delay: i }, opacity: { duration: 0.15, delay: i }, x: { duration: 0.4 }, y: { duration: 0.4 } }}>
    <motion.img src={f.img} alt={f.name} draggable={false} initial={false}
      animate={{ rotateX: flat ? -14 : -78, opacity: flat ? 0 : 1 }} transition={{ rotateX: { duration: 0.45, delay: flat ? 0.1 : 0, ease: [0.22, 1, 0.36, 1] }, opacity: { duration: 0.25, delay: flat ? 0.75 : 0 } }}
      style={{ height: bh, width: "auto", maxWidth: "none", transformOrigin: "50% 100%", filter: "drop-shadow(0 -6px 8px rgba(0,0,0,.35))" }} />
  </motion.div>
);

const Flap = ({ side, closed }) => (
  <motion.div className="absolute top-0" style={{ width: W / 2, height: D, left: side === "l" ? 0 : W / 2, transformOrigin: side === "l" ? "0% 50%" : "100% 50%",
    background: side === "l" ? "#2A2247" : "#231C3D", boxShadow: "inset 0 0 0 2px #3a3160", backfaceVisibility: "visible" }}
    initial={false}
    animate={{ opacity: closed ? 1 : 0, rotateY: closed ? 0 : side === "l" ? -115 : 115, z: WALL }}
    transition={{ rotateY: { duration: 0.75, delay: closed ? 0.55 + (side === "r" ? 0.12 : 0) : 0, ease: [0.34, 1.3, 0.64, 1] }, opacity: { duration: 0.2, delay: closed ? 0.5 : 0.5 } }} />
);

const COLORS = ["#FF5A5F", "#FFB21A", "#3EE6FF", "#7BD88F", "#FF6F91", "#7CC4FF"];
const PIECES = Array.from({ length: 30 }, (_, i) => ({ i, a: (i / 30) * Math.PI * 2, d: 120 + ((i * 37) % 110), r: (i * 53) % 360, c: COLORS[i % COLORS.length], w: 6 + (i % 3) * 3 }));

const Confetti = () => (
  <div data-testid="box-confetti" className="pointer-events-none absolute left-1/2 top-1/2 z-30">
    {PIECES.map((p) => (
      <motion.span key={p.i} className="absolute block rounded-[2px]" style={{ width: p.w, height: p.w * 1.6, background: p.c }}
        initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0.4 }}
        animate={{ x: Math.cos(p.a) * p.d * 1.6, y: [0, Math.sin(p.a) * p.d - 60, Math.sin(p.a) * p.d + 90], opacity: [1, 1, 0], rotate: p.r + 360, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut", delay: 1.25 }} />
    ))}
  </div>
);

export const BoxScene = ({ size, placed, onAdd, canAdd = true, addLabel = "Add to cart" }) => {
  const wrap = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => {
      const maxH = window.innerWidth < 1024 ? 190 : 320;
      setScale(Math.min(1, e.contentRect.width / (SCENE_W + 40), maxH / SCENE_H));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [cols, rows] = GRID[size] || [6, 4];
  const cw = (W - 24) / cols;
  const bh = (cw * 0.92) / 0.95;
  const rd = (D - 20) / rows;
  const full = placed.length === size && size > 0;
  const sig = placed.map((p) => p.id).join(",");
  const [peek, setPeek] = useState(false);
  const [sealed, setSealed] = useState(false);
  const [left, setLeft] = useState(5);
  useEffect(() => {
    setPeek(false);
    setSealed(false);
    if (!full) return undefined;
    setLeft(5);
    const iv = setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    const t = setTimeout(() => setSealed(true), 5000);
    return () => { clearInterval(iv); clearTimeout(t); };
  }, [full, sig]);
  const closed = sealed && !peek;
  const split = FLAVOURS.map((f) => ({ f, n: placed.filter((p) => p.f.key === f.key).length })).filter((x) => x.n);

  return (
    <div ref={wrap} data-testid="box-visual" className="relative w-full overflow-hidden rounded-[28px] bg-[#BDE3FA]" style={{ height: SCENE_H * scale }}>
      <span data-testid="box-visual-count" className={`absolute top-3 left-3 z-20 rounded-full px-3.5 py-1 text-sm font-bold shadow-sm transition-colors ${closed ? "bg-[#7BD88F] text-[#1B1530]" : "bg-white"}`}>
        {closed ? "Your box is ready!" : full && !sealed ? `Here's your mix! Packing in ${left}…` : `${placed.length}/${size} in the box`}
      </span>
      {full && !sealed && (
        <button data-testid="box-pack-now" onClick={() => setSealed(true)}
          className="absolute top-3 right-3 z-20 rounded-full bg-[#1B1530] text-white px-3.5 py-1 text-sm font-bold hover:bg-[#FF5A5F] transition-colors">Pack it now</button>
      )}
      {sealed && (
        <button data-testid="box-peek-toggle" onClick={() => setPeek((v) => !v)}
          className="absolute top-3 right-3 z-20 rounded-full bg-[#1B1530] text-white px-3.5 py-1 text-sm font-bold hover:bg-[#FF5A5F] transition-colors">
          {peek ? "Close box" : "Peek inside"}
        </button>
      )}
      {full && !closed && split.length > 0 && (
        <div data-testid="box-split" className="absolute bottom-3 inset-x-3 z-20 flex flex-wrap justify-center gap-1.5">
          {split.map(({ f, n }) => (
            <span key={f.key} className="rounded-full bg-white/95 px-2.5 py-1 text-xs font-bold shadow-sm" style={{ boxShadow: `inset 3px 0 0 ${f.color}` }}>{n} {f.name}</span>
          ))}
        </div>
      )}
      {closed && onAdd && (
        <motion.button data-testid="box-scene-add-to-cart" onClick={onAdd} disabled={!canAdd}
          initial={{ opacity: 0, y: 16, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} transition={{ delay: 1.4, duration: 0.4 }}
          className="absolute bottom-4 left-1/2 z-30 h-12 px-7 rounded-full bg-[#FF5A5F] text-white font-bold shadow-lg hover:bg-[#1B1530] transition-colors disabled:opacity-50">
          {addLabel}
        </motion.button>
      )}
      <AnimatePresence>{closed && <Confetti key={`c-${size}`} />}</AnimatePresence>
      <div className="absolute left-1/2 top-0 origin-top" style={{ width: SCENE_W, height: SCENE_H, transform: `translateX(-50%) scale(${scale})`, perspective: 1300, perspectiveOrigin: "50% 0%" }}>
        <div className="absolute" style={{ width: W, height: D, left: (SCENE_W - W) / 2, top: 70, transformStyle: "preserve-3d", transform: "rotateX(56deg)" }}>
          <div className="absolute inset-0 rounded-sm" style={{ background: "#120E22", boxShadow: "0 0 0 2px #2a2347 inset" }} />
          <div className="absolute rounded-full" style={{ inset: -40, background: "radial-gradient(closest-side, rgba(27,21,48,.35), transparent)", transform: "translateZ(-2px)" }} />
          <Wall shade="#231C3D" style={{ left: 0, top: -WALL, width: W, height: WALL, transformOrigin: "50% 100%", transform: "rotateX(-90deg)" }} />
          <Wall shade="#2A2247" style={{ left: 0, top: 0, width: WALL, height: D, transformOrigin: "0% 50%", transform: "rotateY(-90deg)" }} />
          <Wall shade="#2A2247" style={{ left: W - WALL, top: 0, width: WALL, height: D, transformOrigin: "100% 50%", transform: "rotateY(90deg)" }} />
          <AnimatePresence>
            {placed.map((p, idx) => {
              const r = Math.floor(idx / cols);
              const c = idx % cols;
              return <Bag key={p.id} f={p.f} x={12 + c * cw} rowY={10 + (r + 0.85) * rd} cw={cw} bh={bh} i={p.delay || 0} flat={closed} />;
            })}
          </AnimatePresence>
          <Wall shade="#1B1530" style={{ left: 0, top: D - WALL, width: W, height: WALL, transformOrigin: "50% 100%", transform: "rotateX(-90deg) translateZ(1px)" }}>
            <img src="/img/logo-new.webp" alt="Swole Bears" style={{ height: WALL * 0.86, width: "auto" }} />
          </Wall>
          <Flap side="l" closed={closed} />
          <Flap side="r" closed={closed} />
        </div>
      </div>
    </div>
  );
};
